import { useMutation, useQuery } from '@apollo/client';
import { EyeIcon, GlobeAltIcon, TrashIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/buttons/Button';
import Modal from '../../../components/modal/Modal';
import Table from '../../../components/table/Table';
import { useToast } from '../../../components/toast/ToastProvider';
import { Link, Subtitle, Title } from '../../../components/typography';
import { DELETE_DOMAIN } from '../../../graphql/domains/mutations';
import { GET_DOMAIN, GET_DOMAINS } from '../../../graphql/domains/queries';
import Form from './sharedComponents/Form';

function Domains() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data } = useQuery(GET_DOMAINS);
  const [open, setOpen] = useState(false);
  const [deleteDomain] = useMutation(DELETE_DOMAIN);
  const { data: domain } = useQuery(GET_DOMAIN, {
    variables: { _id }
  });

  const handleClick = async (id) => {
    const res = await deleteDomain({
      variables: { _id: id },
      refetchQueries: [{ query: GET_DOMAINS }, 'GetDomains']
    });

    toast({
      title: res.data?.deleteDomain?.error ? 'Domain not deleted' : 'Domain deleted',
      description:
        res.data?.deleteDomain?.message ||
        `The domain name ${res.data?.deleteDomain?.name} has been deleted successfully.`,
      type: res.data?.deleteDomain?.error ? 'error' : 'success'
    });
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'Name'
      },
      {
        accessor: 'createdBy',
        Header: 'Creator',
        headerClassName: 'text-center',
        className: 'text-center',
        Cell: ({ value }) => {
          return <Link href={`mailto:${value}`} text={value} />;
        }
      },
      {
        accessor: 'createdAt',
        Header: 'Date',
        Cell: ({ value }) => {
          return <>{dayjs(value).format('LLL')}</>;
        }
      },
      {
        accessor: 'actions',
        Header: '',
        Cell: ({ row }) => {
          return (
            <div className="flex items-center justify-center space-x-2">
              <Button
                type="button"
                color="text-red-500"
                Icon={TrashIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  toast({
                    type: 'warning',
                    title: 'Delete domain name',
                    autoClose: false,
                    description: 'Are you sure you want to delete this domain name?',
                    onAccept: () => handleClick(row.original._id)
                  });
                }}
              />
              <Button
                type="button"
                color="text-sky-500"
                Icon={EyeIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/employees/domains/${row.original._id}`);
                }}
              />
            </div>
          );
        },
        disableSortBy: true
      }
    ],
    []
  );

  useEffect(() => {
    if (_id) setOpen(true);
  }, [_id]);

  return (
    <>
      <Modal
        open={open}
        closeModal={() => setOpen(false)}
        title="Domain Name Form"
        description="Use the following request form to create or edit a domain name."
        onExitComplete={() => {
          if (_id) {
            navigate('/employees/domains/');
          }
        }}
      >
        <Form onSuccess={() => setOpen(false)} data={domain?.domain} />
      </Modal>
      <Title text="Domains" />
      <Subtitle text="This section is intended for managing employees domain names. Please use this section to create, read, update or delete domain names." />
      <Table
        columns={columns}
        data={data?.domains || []}
        onRowClick={(row) => navigate(`/employees/domains/${row?._id}`)}
        toolbar={
          <div className="flex items-center">
            <Button
              label="New Domain Name"
              Icon={GlobeAltIcon}
              backgroundColor="bg-green-500"
              onClick={() => setOpen(true)}
            />
          </div>
        }
      />
    </>
  );
}

export default Domains;
